export type MoneyInputValue = number | null;

export const formatMoneyInputValue = (
  value: MoneyInputValue | undefined
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '';
  }

  return value.toFixed(2).replace('.', ',');
};

export const sanitizeMoneyInput = (
  value: string,
  allowGrouping = false
): string => {
  const stripped = value.replace(/\s/g, '');
  const lastSeparator = Math.max(
    stripped.lastIndexOf('.'),
    stripped.lastIndexOf(',')
  );
  const separators = stripped.match(/[.,]/g) || [];
  const integerPart = (
    lastSeparator >= 0 ? stripped.slice(0, lastSeparator) : stripped
  ).replace(/\D/g, '');
  const fractionDigits =
    lastSeparator >= 0
      ? stripped.slice(lastSeparator + 1).replace(/\D/g, '')
      : '';
  const fractionPart = fractionDigits.slice(0, 2);

  if (!integerPart && !fractionPart) return '';
  if (lastSeparator < 0) return integerPart;
  if (
    allowGrouping &&
    fractionDigits.length === 3 &&
    separators.every((separator) => separator === stripped[lastSeparator])
  ) {
    return `${integerPart}${fractionDigits}`;
  }

  return `${integerPart || '0'}.${fractionPart}`;
};

export const transformMoneyInputValue = (
  value: string,
  preferCents = false,
  allowGrouping = false
): string => {
  const strippedValue = value.replace(/\s/g, '');

  if (!strippedValue) return '';

  if (preferCents || !/[.,]/.test(strippedValue)) {
    const digits = strippedValue.replace(/\D/g, '');

    if (!digits) return '';

    const paddedDigits = digits.padStart(3, '0');
    const integerPart = paddedDigits.slice(0, -2).replace(/^0+(?=\d)/, '');

    return `${integerPart}.${paddedDigits.slice(-2)}`;
  }

  return sanitizeMoneyInput(strippedValue, allowGrouping);
};

export const parseMoneyInputValue = (value: string): MoneyInputValue => {
  const normalized = transformMoneyInputValue(value, false, true);

  if (!normalized || !/\d/.test(normalized)) return null;

  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : null;
};
